import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin, getUserProfile } from '../utils/api';
import { 
  setAuth, 
  clearAuth, 
  getUser, 
  isAuthenticated, 
  isTokenExpired 
} from '../utils/auth';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (isAuthenticated() && !isTokenExpired()) {
          // If token exists and is not expired, get current user data
          const storedUser = getUser();
          if (storedUser) {
            setUser(storedUser);
            
            // Optionally refresh user data from API
            try {
              const userData = await getUserProfile();
              setUser(userData);
            } catch (err) {
              console.warn('Could not refresh user profile:', err);
              // Continue with stored user data
            }
          }
        } else if (isTokenExpired()) {
          // Clear auth if token is expired
          clearAuth();
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError('Authentication failed. Please log in again.');
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      const data = await apiLogin(email, password);
      
      // Store auth data
      setAuth({
        token: data.token,
        user: data.user
      });
      
      // Update state
      setUser(data.user);
      
      return data.user;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
      throw err;
    }
  };

  // Logout function
  const logout = () => {
    clearAuth();
    setUser(null);
  };

  // Update user profile in context
  const updateUserProfile = (updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));
    
    // Also update in localStorage
    const storedUser = getUser();
    if (storedUser) {
      setAuth({
        user: {
          ...storedUser,
          ...updatedUserData
        }
      });
    }
  };

  // Auth context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUserProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
